import { reactive } from 'vue';
import { EventMessageUtils, EventType, InteractionStatus } from '@azure/msal-browser';

/**
 * Helper function to determine whether 2 arrays are equal
 * Used to avoid unnecessary state updates
 * @param arrayA
 * @param arrayB
 */
function accountArraysAreEqual(arrayA, arrayB) {
  if (arrayA.length !== arrayB.length) {
    return false;
  }

  const comparisonArray = [...arrayB];

  return arrayA.every((elementA) => {
    const elementB = comparisonArray.shift();
    if (!elementA || !elementB) {
      return false;
    }

    return elementA.homeAccountId === elementB.homeAccountId && elementA.localAccountId === elementB.localAccountId && elementA.username === elementB.username;
  });
}

export const msalPlugin = {
  install: (app, msalInstance) => {
    const inProgress = InteractionStatus.Startup;
    const accounts = msalInstance.getAllAccounts();

    const state = reactive({
      instance: msalInstance,
      inProgress: inProgress,
      accounts: accounts,
      // トークン管理状態
      tokenRefreshInProgress: false,
      lastTokenRefresh: null,
      tokenRefreshError: null,
    });

    app.config.globalProperties.$msal = state;

    // トークン期限切れチェック関数
    const checkTokenExpiration = () => {
      if (state.accounts.length > 0) {
        const account = state.accounts[0];
        if (account.idTokenClaims && account.idTokenClaims.exp) {
          const expirationTime = account.idTokenClaims.exp * 1000; // Convert to milliseconds
          const currentTime = Date.now();
          const timeUntilExpiration = expirationTime - currentTime;

          // トークンが5分以内に期限切れになる場合、事前にリフレッシュを試行
          if (timeUntilExpiration < 5 * 60 * 1000 && timeUntilExpiration > 0) {
            console.log('Token expiring soon, attempting preemptive refresh...');
            performSilentTokenRefresh();
          }
        }
      }
    };

    // サイレントトークンリフレッシュ実行
    const performSilentTokenRefresh = async () => {
      if (state.tokenRefreshInProgress) {
        console.log('Token refresh already in progress, skipping...');
        return;
      }

      try {
        state.tokenRefreshInProgress = true;
        state.tokenRefreshError = null;

        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          const request = {
            account: accounts[0],
            scopes: ['openid', 'email'],
            forceRefresh: true,
          };

          console.log('Attempting silent token refresh...');
          const response = await msalInstance.acquireTokenSilent(request);
          console.log('Silent token refresh successful');
          state.lastTokenRefresh = Date.now();

          // Vue アプリ全体にトークンリフレッシュ成功を通知
          app.config.globalProperties.$emit?.('msal:tokenRefreshed', response);
        }
      } catch (error) {
        console.warn('Silent token refresh failed:', error);
        state.tokenRefreshError = error;

        // サイレントリフレッシュが失敗した場合、アプリ全体に通知
        app.config.globalProperties.$emit?.('msal:tokenRefreshFailed', error);
      } finally {
        state.tokenRefreshInProgress = false;
      }
    };

    // 定期的なトークン期限チェック（1分間隔）
    setInterval(checkTokenExpiration, 60 * 1000);

    msalInstance.addEventCallback((message) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('MSAL Event:', message.eventType, message);
      }
      switch (message.eventType) {
        case EventType.ACCOUNT_ADDED:
        case EventType.ACCOUNT_REMOVED:
        case EventType.LOGIN_SUCCESS:
        case EventType.SSO_SILENT_SUCCESS:
        case EventType.HANDLE_REDIRECT_END:
        case EventType.LOGIN_FAILURE:
        case EventType.LOGOUT_END: {
          const currentAccounts = msalInstance.getAllAccounts();
          if (!accountArraysAreEqual(currentAccounts, state.accounts)) {
            state.accounts = currentAccounts;
          }
          break;
        }

        // トークン取得成功時の処理
        case EventType.ACQUIRE_TOKEN_SUCCESS: {
          console.log('Token acquired successfully');
          state.lastTokenRefresh = Date.now();
          state.tokenRefreshError = null;
          // Vue アプリ全体に通知
          app.config.globalProperties.$emit?.('msal:tokenAcquired', message.payload);
          break;
        }

        // トークン取得失敗時の処理
        case EventType.ACQUIRE_TOKEN_FAILURE: {
          console.warn('Token acquisition failed:', message.error);
          state.tokenRefreshError = message.error;

          // インタラクションが必要なエラーの場合、ユーザーに通知
          if (message.error?.errorCode === 'interaction_required') {
            console.log('User interaction required for token refresh');
            app.config.globalProperties.$emit?.('msal:interactionRequired', message.error);
          }
          break;
        }

        // サイレントトークン取得失敗時の処理
        case EventType.SSO_SILENT_FAILURE: {
          console.warn('Silent SSO failed:', message.error);
          // 必要に応じてインタラクティブログインにフォールバック
          if (message.error?.errorCode === 'interaction_required') {
            console.log('Silent refresh failed, interaction may be required');
            app.config.globalProperties.$emit?.('msal:silentRefreshFailed', message.error);
          }
          break;
        }
      }

      const status = EventMessageUtils.getInteractionStatusFromEvent(message, state.inProgress);
      if (status !== null) {
        state.inProgress = status;
      }
    });

    // 初期トークンチェック
    setTimeout(checkTokenExpiration, 1000);
  },
};
